import { useState } from "react";
import { Link } from "react-router-dom";
import { Paperclip } from "lucide-react";
import { useFileMessage, useIgnoreMessage, type MessageBrief } from "@/api/mail";
import { useOperationsBoard } from "@/api/operations";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/field";
import { formatDate } from "@/lib/format";

const STATUS: Record<MessageBrief["status"], "muted" | "success" | "secondary"> = {
  new: "muted",
  filed: "success",
  ignored: "secondary",
};

export function MessageRow({ message, onOpen }: { message: MessageBrief; onOpen: () => void }) {
  const [filing, setFiling] = useState(false);
  const [callId, setCallId] = useState("");
  const board = useOperationsBoard({ includeDeparted: true });
  const file = useFileMessage();
  const ignore = useIgnoreMessage();
  return (
    <li className="flex flex-col gap-2 rounded-lg border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <button type="button" className="min-w-0 text-left" onClick={onOpen}>
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <span className="truncate">{message.subject || "(no subject)"}</span>
            <Badge variant={STATUS[message.status]}>{message.status}</Badge>
            {message.is_ours && <Badge variant="secondary">ours</Badge>}
            {message.attachment_count > 0 && (
              <Badge variant="secondary">
                <Paperclip className="h-3 w-3" aria-hidden /> {message.attachment_count}
              </Badge>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {message.from_name || message.from_addr} · {formatDate(message.sent_at)}
            {message.vessel_call_id && (
              <>
                {" · "}
                <Link to="/operations" className="text-primary hover:underline">
                  on the board
                </Link>
              </>
            )}
          </p>
        </button>
        {message.status !== "ignored" && !filing && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setFiling(true)}>
              File under…
            </Button>
            {message.status === "new" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={ignore.isPending}
                onClick={() => ignore.mutate(message.id)}
              >
                Ignore
              </Button>
            )}
          </div>
        )}
      </div>
      <RequestError error={file.error ?? ignore.error} />
      {filing && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <SelectField
            id={`file-${message.id}`}
            label="Vessel call"
            value={callId}
            onChange={setCallId}
            placeholder="Choose a call"
            options={(board.data ?? []).map((call) => ({
              value: call.vessel_call_id,
              label: `${call.vessel_name} · ${call.port} · ${formatDate(call.arrival)}`,
            }))}
            className="sm:flex-1"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!callId || file.isPending}
              onClick={() =>
                file.mutate(
                  { id: message.id, vesselCallId: callId },
                  { onSuccess: () => setFiling(false) },
                )
              }
            >
              File
            </Button>
            <Button size="sm" variant="outline" onClick={() => setFiling(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
